import { vetor, type Vetor3 } from '../geometria/vetor3'
import {
  ancoraPorId,
  montanteColunaC,
  zDaColunaNaAltura,
  type ConfiguracaoSrs,
  type PerfilVeiculo,
} from './perfilVeiculo'

/**
 * Geometria do SRS de um perfil: módulo fechado, bolsa inflada e gerador de gás
 * de cada airbag declarado.
 *
 * Fica no domínio pelo mesmo motivo de `pontosCinto`: são posições derivadas do
 * perfil, e o teste é o que garante que nenhum gerador cai fora da carroceria
 * ou longe da coluna que o perfil declara — coisa que a perspectiva esconde.
 */

export type ChaveSrs = keyof ConfiguracaoSrs

/**
 * Onde fica o gerador de gás da cortina. Varia muito de veículo para veículo,
 * por isso é configuração do perfil, nunca uma posição assumida.
 */
export type PosicaoGeradorCortina = 'colunaA' | 'colunaB' | 'colunaC' | 'trilhoMeio'

/**
 * - `almofada`: bolsa frontal, lateral, de banco ou de coluna — elipsoide achatado
 * - `cortina`: painel acolchoado em câmaras, que desce do trilho do teto
 */
export type FormaBolsa = 'almofada' | 'cortina'

/**
 * - `disco`: gerador pirotécnico do volante
 * - `tubo`: gerador curto do painel, das laterais, do banco e da coluna
 * - `cilindroLongo`: gerador de gás armazenado da cortina
 */
export type FormaGerador = 'disco' | 'tubo' | 'cilindroLongo'

export interface BolsaSrs {
  forma: FormaBolsa
  /** centro da bolsa totalmente inflada */
  centro: Vetor3
  /** largura (x), altura (y) e profundidade (z) da bolsa totalmente inflada */
  dimensoes: readonly [number, number, number]
  /** inclinação em torno de x, para a bolsa do volante acompanhar o aro */
  inclinacaoX: number
}

export interface GeradorSrs {
  id: string
  forma: FormaGerador
  posicao: Vetor3
  raio: number
  comprimento: number
  /** eixo do cilindro */
  eixo: 'x' | 'y' | 'z'
}

export interface ModuloSrs {
  chave: ChaveSrs
  /** centro do módulo fechado */
  posicao: Vetor3
  dimensoesModulo: readonly [number, number, number]
  bolsa: BolsaSrs
  gerador: GeradorSrs
}

/** Id de catálogo do gerador de gás de um airbag: `airbagMotorista` → `geradorAirbagMotorista`. */
export const idGeradorDoAirbag = (chave: string): string =>
  `gerador${chave.charAt(0).toUpperCase()}${chave.slice(1)}`

/** Inclinação do aro do volante (ver `Habitaculo`), que a bolsa acompanha. */
const INCLINACAO_VOLANTE_RAD = (24 * Math.PI) / 180

/** Módulos SRS do perfil, só os que ele declara. */
export function modulosSrs(perfil: PerfilVeiculo): ModuloSrs[] {
  return todosOsModulos(perfil).filter((m) => perfil.srs[m.chave])
}

function todosOsModulos(perfil: PerfilVeiculo): ModuloSrs[] {
  const motorista = ancoraPorId(perfil, 'motorista')
  const passageiro = ancoraPorId(perfil, 'passageiro')
  const zPainel = perfil.colunaA.zBase - 0.22
  const yPainel = perfil.cinturaY - 0.13
  // trilho do teto: um pouco para dentro da chapa lateral
  const mx = perfil.largura / 2 - 0.1

  const volante = vetor(motorista.posicao.x, perfil.cinturaY - 0.1, perfil.colunaA.zBase - 0.38)
  const painel = vetor(passageiro.posicao.x, yPainel, zPainel)

  // sinal +1 é a esquerda (+x), como em toda a maquete
  const lateral = (sinal: 1 | -1, ancoraY: number, ancoraZ: number): Pick<ModuloSrs, 'posicao' | 'dimensoesModulo' | 'bolsa'> => {
    const posicao = vetor(sinal * (mx - 0.05), ancoraY + 0.34, ancoraZ - 0.14)
    return {
      posicao,
      dimensoesModulo: [0.07, 0.16, 0.1],
      bolsa: {
        forma: 'almofada',
        centro: vetor(posicao.x - sinal * 0.1, posicao.y + 0.04, posicao.z + 0.14),
        dimensoes: [0.1, 0.42, 0.34],
        inclinacaoX: 0,
      },
    }
  }

  const cortina = (sinal: 1 | -1): Pick<ModuloSrs, 'posicao' | 'dimensoesModulo' | 'bolsa'> => {
    const zFrente = perfil.colunaA.zTopo
    const zTras = perfil.colunaC.zTopo
    const yTopo = perfil.tetoY - 0.08
    const yBase = perfil.cinturaY - 0.05
    return {
      posicao: vetor(sinal * mx, yTopo, (zFrente + zTras) / 2),
      dimensoesModulo: [0.06, 0.06, zFrente - zTras],
      bolsa: {
        forma: 'cortina',
        centro: vetor(sinal * (mx - 0.04), (yTopo + yBase) / 2, (zFrente + zTras) / 2),
        dimensoes: [0.07, yTopo - yBase, zFrente - zTras],
        inclinacaoX: 0,
      },
    }
  }

  const banco = vetor(motorista.posicao.x + 0.2, motorista.alturaAssento + 0.2, motorista.posicao.z - 0.18)
  const coluna = vetor(mx, perfil.cinturaY + 0.18, perfil.colunaB.z)

  const modulos: Array<Omit<ModuloSrs, 'gerador'> & { gerador: Omit<GeradorSrs, 'id'> }> = [
    {
      chave: 'airbagMotorista',
      posicao: volante,
      dimensoesModulo: [0.14, 0.05, 0.14],
      bolsa: {
        forma: 'almofada',
        // abre do cubo para trás e para cima, seguindo o aro inclinado, até
        // cobrir o rosto do motorista sentado
        centro: vetor(volante.x, volante.y + 0.16, volante.z - 0.17),
        dimensoes: [0.6, 0.64, 0.24],
        inclinacaoX: -INCLINACAO_VOLANTE_RAD,
      },
      gerador: { forma: 'disco', posicao: volante, raio: 0.04, comprimento: 0.03, eixo: 'z' },
    },
    {
      chave: 'airbagPassageiro',
      posicao: painel,
      dimensoesModulo: [0.2, 0.06, 0.12],
      bolsa: {
        forma: 'almofada',
        // ocupa o vão entre o painel e o para-brisa, crescendo para o ocupante
        centro: vetor(painel.x, painel.y + 0.16, painel.z - 0.24),
        dimensoes: [0.62, 0.5, 0.46],
        inclinacaoX: 0,
      },
      gerador: { forma: 'tubo', posicao: vetor(painel.x, painel.y - 0.04, painel.z), raio: 0.026, comprimento: 0.17, eixo: 'x' },
    },
    {
      chave: 'airbagLateralEsquerdo',
      ...lateral(1, motorista.alturaAssento, motorista.posicao.z),
      gerador: { forma: 'tubo', posicao: vetor(mx - 0.05, motorista.alturaAssento + 0.3, motorista.posicao.z - 0.14), raio: 0.016, comprimento: 0.09, eixo: 'y' },
    },
    {
      chave: 'airbagLateralDireito',
      ...lateral(-1, passageiro.alturaAssento, passageiro.posicao.z),
      gerador: { forma: 'tubo', posicao: vetor(-(mx - 0.05), passageiro.alturaAssento + 0.3, passageiro.posicao.z - 0.14), raio: 0.016, comprimento: 0.09, eixo: 'y' },
    },
    { chave: 'airbagCortinaEsquerdo', ...cortina(1), gerador: geradorCortina(perfil, 1, mx) },
    { chave: 'airbagCortinaDireito', ...cortina(-1), gerador: geradorCortina(perfil, -1, mx) },
    {
      chave: 'airbagBancoMotorista',
      posicao: banco,
      dimensoesModulo: [0.06, 0.14, 0.08],
      bolsa: {
        forma: 'almofada',
        centro: vetor(banco.x + 0.08, banco.y + 0.06, banco.z + 0.12),
        dimensoes: [0.09, 0.38, 0.3],
        inclinacaoX: 0,
      },
      gerador: { forma: 'tubo', posicao: vetor(banco.x, banco.y - 0.03, banco.z), raio: 0.016, comprimento: 0.08, eixo: 'y' },
    },
    {
      chave: 'airbagColunaMotorista',
      posicao: coluna,
      dimensoesModulo: [0.07, 0.12, 0.07],
      bolsa: {
        forma: 'almofada',
        centro: vetor(coluna.x - 0.07, coluna.y - 0.02, coluna.z + 0.06),
        dimensoes: [0.1, 0.34, 0.14],
        inclinacaoX: 0,
      },
      gerador: { forma: 'tubo', posicao: vetor(coluna.x, coluna.y - 0.03, coluna.z), raio: 0.016, comprimento: 0.08, eixo: 'y' },
    },
  ]
  return modulos.map((m) => ({ ...m, gerador: { ...m.gerador, id: idGeradorDoAirbag(m.chave) } }))
}

/** Gerador de gás armazenado da cortina, na posição que o perfil declara. */
function geradorCortina(perfil: PerfilVeiculo, sinal: 1 | -1, mx: number): Omit<GeradorSrs, 'id'> {
  const x = sinal * (mx - 0.02)
  const base = { forma: 'cilindroLongo' as const, raio: 0.018, comprimento: 0.24 }
  // nas colunas o cilindro desce pelo acabamento; no trilho ele corre junto ao teto
  const yColuna = perfil.tetoY - 0.16
  switch (perfil.geradorCortina) {
    case 'colunaA':
      return { ...base, posicao: vetor(x, yColuna, zDaColunaNaAltura(perfil, perfil.colunaA, yColuna) - 0.03), eixo: 'y' }
    case 'colunaB':
      return { ...base, posicao: vetor(x, yColuna, perfil.colunaB.z), eixo: 'y' }
    case 'colunaC':
      return { ...base, posicao: vetor(x, yColuna, zDaColunaNaAltura(perfil, montanteColunaC(perfil), yColuna) + 0.03), eixo: 'y' }
    case 'trilhoMeio':
      return { ...base, posicao: vetor(x, perfil.tetoY - 0.07, perfil.colunaB.z), eixo: 'z' }
  }
}
